import type { ButtonHTMLAttributes, HTMLAttributes, ReactNode } from 'react'

export function cx(...parts: (string | false | null | undefined)[]): string {
  return parts.filter(Boolean).join(' ')
}

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger'
type Size = 'sm' | 'md'

const VARIANTS: Record<Variant, string> = {
  // The gradient is brand punctuation, reserved for the primary action on a screen.
  primary: 'brand-gradient text-white border-transparent hover:opacity-90 shadow-card',
  secondary: 'bg-surface text-ink border-line hover:bg-surface-2',
  ghost: 'bg-transparent text-ink-2 border-transparent hover:bg-sunken',
  danger: 'bg-crit-wash text-crit border-crit/30 hover:bg-crit hover:text-white',
}

const SIZES: Record<Size, string> = {
  sm: 'h-8 px-3 text-[13px] gap-1.5',
  md: 'h-10 px-4 text-sm gap-2',
}

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant
  size?: Size
  loading?: boolean
}

export function Button({
  variant = 'secondary',
  size = 'md',
  loading = false,
  className,
  children,
  disabled,
  ...rest
}: ButtonProps) {
  return (
    <button
      // Explicit, because a button inside a form defaults to submit and quietly
      // posts the form when it was only ever meant to open a dialog.
      type="button"
      disabled={disabled || loading}
      className={cx(
        'inline-flex items-center justify-center rounded-md border font-medium',
        'transition-colors disabled:opacity-50 disabled:pointer-events-none',
        VARIANTS[variant],
        SIZES[size],
        className,
      )}
      {...rest}
    >
      {loading && <Spinner className="size-3.5" />}
      {children}
    </button>
  )
}

export function ChevronLeft({ className }: { className?: string }) {
  return (
    <svg aria-hidden viewBox="0 0 12 12" className={cx('size-3 shrink-0', className)}>
      <path
        d="M7.5 2 3.5 6l4 4"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

/**
 * A bare icon that happens to be clickable: no border, no fill, no button chrome.
 *
 * The chrome is what made the header's controls read as small - a 16px glyph inside a
 * 32px box next to a 28px avatar looks like a mistake, and drawing a box around an icon
 * that never has a resting background only advertises how much smaller it is than the
 * thing beside it. The glyph is 20px and the box is invisible until it is hovered, which
 * is also where the keyboard focus lands - the target stays 36px whatever it looks like.
 */
export function IconButton({
  label,
  title,
  onClick,
  className,
  children,
  ...rest
}: {
  /** For a screen reader. The icon carries the meaning visually; this carries it otherwise. */
  label: string
  title?: string
  onClick: () => void
  className?: string
  children: ReactNode
} & Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'onClick' | 'title' | 'className' | 'children'>) {
  return (
    <button
      type="button"
      title={title ?? label}
      aria-label={label}
      onClick={onClick}
      className={cx(
        'relative grid size-9 shrink-0 place-items-center rounded-md text-ink-2',
        'transition-colors hover:bg-sunken hover:text-ink',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/40',
        className,
      )}
      {...rest}
    >
      <span className="grid size-5 place-items-center">{children}</span>
    </button>
  )
}

/**
 * Pass <c>label={null}</c> when visible text already says the page is loading - two
 * announcements of the same thing is worse than one.
 */
export function Spinner({ className, label = 'Loading' }: { className?: string; label?: string | null }) {
  return (
    <span
      role={label === null ? undefined : 'status'}
      aria-label={label ?? undefined}
      aria-hidden={label === null || undefined}
      className={cx(
        'inline-block animate-spin rounded-full border-2 border-current border-t-transparent',
        className ?? 'size-4',
      )}
    />
  )
}

type Tone = 'neutral' | 'positive' | 'warning' | 'critical' | 'accent' | 'spark'

const TONES: Record<Tone, string> = {
  neutral: 'bg-sunken text-muted',
  positive: 'bg-pos-wash text-pos',
  warning: 'bg-warn-wash text-warn',
  critical: 'bg-crit-wash text-crit',
  accent: 'bg-accent-wash text-accent',

  // The second half of the brand gradient. Used for the override tier, which is neither good
  // news nor a warning - it is a different kind of earning, and it reads as one.
  spark: 'bg-spark-wash text-spark',
}

export function Badge({ tone = 'neutral', children }: { tone?: Tone; children: ReactNode }) {
  return (
    <span
      className={cx(
        'inline-flex items-center rounded-sm px-2 py-0.5 text-[11px] font-medium',
        'font-mono uppercase tracking-[0.08em] whitespace-nowrap',
        TONES[tone],
      )}
    >
      {children}
    </span>
  )
}

export function Card({ className, children, ...rest }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cx('rounded-lg border border-line bg-surface shadow-card', className)}
      {...rest}
    >
      {children}
    </div>
  )
}

export function CardHeader({ title, hint, action }: { title: string; hint?: string; action?: ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-4 border-b border-line-soft px-5 py-4">
      <div>
        <h2 className="text-base font-semibold">{title}</h2>
        {hint && <p className="mt-0.5 text-[13px] text-muted">{hint}</p>}
      </div>
      {action}
    </div>
  )
}

/**
 * What a screen shows when there is nothing to show. Always says what to do next -
 * an empty table with no explanation reads as a bug.
 */
export function EmptyState({
  title,
  hint,
  action,
}: {
  title: string
  hint?: string
  action?: ReactNode
}) {
  return (
    <div className="flex flex-col items-center gap-3 px-6 py-14 text-center">
      <div className="size-10 rounded-full bg-sunken" aria-hidden />
      <div>
        <p className="font-medium">{title}</p>
        {hint && <p className="mx-auto mt-1 max-w-sm text-[13px] text-muted">{hint}</p>}
      </div>
      {action}
    </div>
  )
}

export function ErrorState({ title, detail, onRetry }: { title: string; detail?: string; onRetry?: () => void }) {
  return (
    <div className="flex flex-col items-start gap-3 rounded-md border border-crit/30 bg-crit-wash px-5 py-4">
      <div>
        <p className="font-medium text-crit">{title}</p>
        {detail && <p className="mt-0.5 text-[13px] text-ink-2">{detail}</p>}
      </div>
      {onRetry && (
        <Button size="sm" onClick={onRetry}>
          Try again
        </Button>
      )}
    </div>
  )
}

/**
 * What a screen shows while it is fetching.
 *
 * A grey block says "there is something here, shaped like this", which is only true if
 * you already know what is coming - and reads as an empty panel if you do not. This says
 * the application is working, in the same words the start-up screen uses, so waiting for
 * a slow page and arriving at a page with nothing on it never look alike.
 */
export function Loading({ label = 'Loading...', className }: { label?: string; className?: string }) {
  return (
    <div
      role="status"
      className={cx('flex items-center justify-center gap-2.5 px-6 py-14 text-muted', className)}
    >
      <Spinner className="size-4" label={null} />
      <span className="text-[13px]">{label}</span>
    </div>
  )
}

/**
 * Wraps content that is being loaded or reloaded, and puts the spinner over it.
 *
 * <p>
 * A loading block rendered above a listing pushes the listing down and lets it spring
 * back, so every reload - a filter change, a page change, a sort - moves the rows out from
 * under the pointer. The overlay leaves the layout exactly where it was.
 * </p>
 *
 * On a first load there is nothing underneath yet, which is why this holds a minimum
 * height while busy: a spinner floating over a collapsed box reads as a broken page.
 *
 * The scrim is translucent rather than opaque, so the rows behind it stay legible while
 * they are being replaced - which is the point of leaving them there.
 */
export function Busy({
  busy,
  label = 'Loading...',
  className,
  children,
}: {
  busy: boolean
  label?: string
  className?: string
  children: ReactNode
}) {
  return (
    <div className={cx('relative', busy && 'min-h-32', className)}>
      {/* aria-busy rather than a live region: the rows are still there and still readable,
          so this is a state on the region, not an announcement replacing it. */}
      <div aria-busy={busy || undefined} className={cx(busy && 'pointer-events-none select-none')}>
        {children}
      </div>

      {busy && (
        <div
          role="status"
          className="absolute inset-0 z-10 flex items-start justify-center rounded-lg bg-surface/65 backdrop-blur-[1px]"
        >
          <span className="mt-14 flex items-center gap-2.5 text-muted">
            <Spinner className="size-4" label={null} />
            <span className="text-[13px]">{label}</span>
          </span>
        </div>
      )}
    </div>
  )
}

export function Skeleton({ className }: { className?: string }) {
  return <div className={cx('animate-pulse rounded-md bg-sunken', className ?? 'h-4 w-full')} />
}
